import { useEffect, useState, type ReactNode } from 'react';
import { isVaultUnlocked, redirectToVault } from '@/lib/vault';

interface Props {
  children: ReactNode;
  /** When true, do not auto-redirect; show a placeholder instead. Useful for landing routes. */
  silent?:  boolean;
}

/**
 * VaultGate — gates broker-dependent UI behind an unlocked vault session.
 *
 * - On mount, checks sessionStorage for the unlocked broker key.
 * - If locked and silent=false, immediately redirects to vault.html with
 *   the current URL as the return target.
 * - If locked and silent=true, renders an inline notice + "Unlock" button.
 * - If unlocked, renders children.
 */
export function VaultGate({ children, silent = false }: Props) {
  const [unlocked, setUnlocked] = useState<boolean | null>(null);

  useEffect(() => {
    const ok = isVaultUnlocked();
    setUnlocked(ok);
    if (!ok && !silent) {
      redirectToVault();
    }
  }, [silent]);

  if (unlocked === null) return null;
  if (!unlocked) {
    if (!silent) return null;
    return (
      <div style={{ padding: 24, color: '#c66b6b', fontFamily: 'Inter, sans-serif' }}>
        <h3 style={{ margin: '0 0 8px' }}>Vault is locked</h3>
        <p style={{ margin: '0 0 14px', color: 'rgba(245,244,238,0.6)' }}>
          Connect a broker by unlocking your encrypted vault. Your broker key is decrypted only on this device.
        </p>
        <button onClick={redirectToVault} style={{
          padding: '9px 16px', background: '#d4a45a', color: '#0a0d0a',
          border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500,
        }}>
          Unlock vault
        </button>
      </div>
    );
  }
  return <>{children}</>;
}
